<template>
  <l-map
    ref="mapRef"
    class="v-map-field"
    :center.sync="mapOpts.center"
    :zoom.sync="mapOpts.zoom"
    :options="{
      zoomControl: false
    }"
  >
    <slot></slot>

    <l-tile-layer :url="mapOpts.url"> </l-tile-layer>

    <l-control-zoom position="topright"></l-control-zoom>
  </l-map>
</template>

<script>
import { reactive, ref, watch } from "vue-demi";
import chroma from "chroma-js";

import { LMap, LTileLayer, LControlZoom } from "vue2-leaflet";

import { abbreviateNumber } from "@/utils/number";

export default {
  props: {
    errorMessages: {
      type: Array,
      default: () => []
    },

    markers: {
      type: Array,
      default: () => []
    },

    displayCluster: {
      type: Function,
      default: () => {}
    }
  },

  components: {
    LMap,
    LTileLayer,
    LControlZoom
  },

  setup(props) {
    const mapRef = ref(null);
    const L = window.L;

    const defaultPanView = {
      center: { lat: 36.2, lng: 3 },
      zoom: 10
    };

    const mapOpts = reactive({
      ...defaultPanView,
      minZoom: 5,
      url:
        "https://api.mapbox.com/styles/v1/mapbox/streets-v11/tiles/{z}/{x}/{y}?access_token=" +
        process.env.VUE_APP_MAPBOX_TOKEN
    });

    const iconCreateFunction = cluster => {
      const { number, ratio, value } = props.displayCluster(cluster);

      const color = chroma.scale([
        "#81c784cb",
        "#cddc39cb",
        "#ffc107cb",
        "#ff5722cb"
      ]);

      const style = {
        background: color(ratio),
        border: `4px solid ${color.gamma(1.5)(ratio)}`
      };

      const styleString = Object.entries(style)
        .map(([k, v]) => `${k}: ${v}`)
        .join("; ");

      const className = ["cluster-icon"];

      if (value !== null) className.push("cluster-with-value");

      const classNameString = className.join(" ");

      return L.divIcon({
        html: `
        <div class='${classNameString}' style='${styleString}' >
          <div>
            ${abbreviateNumber(number)} 
            <strong>${abbreviateNumber(value)}</strong>
          </div>
        </div>`
      });
    };

    let markerClusterLayer = null;

    watch(
      () => props.markers,
      () => {
        if (!mapRef.value) return;

        if (markerClusterLayer) {
          markerClusterLayer.remove();
        }

        markerClusterLayer = L.markerClusterGroup({
          showCoverageOnHover: false,
          maxClusterRadius: 50,
          singleMarkerMode: true,
          iconCreateFunction
        });

        markerClusterLayer.addLayers(props.markers.map(m => L.marker(m)));

        mapRef.value.mapObject.addLayer(markerClusterLayer);

        if (props.markers.length) {
          mapRef.value.mapObject.fitBounds(props.markers);
        }
      },
      { deep: true }
    );

    return {
      mapRef,
      mapOpts
    };
  }
};
</script>

<style lang="scss">
.v-map-field {
  width: 100%;
  height: 100%;

  .leaflet-div-icon {
    background: none;
    border: none;
  }

  .cluster-icon {
    min-width: 32px + 16px + 4px;
    min-height: 32px + 16px + 4px;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 16px;

    strong {
      display: none;
    }

    &.cluster-with-value {
      strong {
        display: block;
      }
    }
  }
}
</style>
